// Kid login (v51) — PIN-based login screen for kid accounts.
//
// Lives at chat2.beemerworks.com (the CF-bypass hostname). The server
// gates `/api/kid-login` by Host header, so on `chat.beemerworks.com`
// the GET probe returns 404 and we render nothing — adults log in
// through their normal Entra-SSO / password flow there.
//
// On chat2, the screen shows:
//   1. Avatar grid of all kid-role users on the instance
//   2. After tap, the per-kid color/emoji fills the screen + PIN keypad
//   3. PIN → POST /api/kid-login → session cookie → app shell takes over

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../icons";
import { api, type KidLoginEntry } from "../lib/rpc";
import type { UseAuth } from "../lib/use-auth";

interface KidLoginViewProps {
  /** Auth hook so we can flip status to logged-in once the kid session
   *  cookie is set. The PIN login doesn't go through `auth.login` (which
   *  is the password path) — we hit the dedicated kid-login endpoint
   *  directly, then nudge the auth hook to re-check whoami. */
  auth: UseAuth;
  /** Caller-supplied "go back to adult login" handler. The kid login
   *  is reachable from the top of the regular login screen; this lets
   *  the user back out if they tapped Kid mode by accident. */
  onBack: () => void;
}

export function KidLoginView({ auth, onBack }: KidLoginViewProps) {
  const [kids, setKids] = useState<KidLoginEntry[] | null>(null);
  const [selected, setSelected] = useState<KidLoginEntry | null>(null);
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  // Grid-level state for tapping a PIN-less kid (signs straight in).
  const [signingInId, setSigningInId] = useState<string | null>(null);
  const [gridErr, setGridErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.kidLoginList()
      .then((list) => { if (!cancelled) setKids(list); })
      .catch((e: Error) => { if (!cancelled) setLoadErr(e.message || "kid login not available"); });
    return () => { cancelled = true; };
  }, []);

  // Tap a face: PIN-protected kids go to the keypad; PIN-less kids sign
  // in immediately (the server only allows this under an authenticated
  // kid-device session, so a bare tap can't mint a session otherwise).
  const onPick = async (k: KidLoginEntry) => {
    if (k.hasPin) {
      setSelected(k);
      return;
    }
    if (signingInId) return;
    setSigningInId(k.id);
    setGridErr(null);
    try {
      await api.kidLogin(k.id);
      await auth.refresh();
    } catch (e) {
      setGridErr((e as Error).message || "couldn't sign in");
      setSigningInId(null);
    }
  };

  const submit = async () => {
    if (!selected || pin.length < 4 || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.kidLogin(selected.id, pin);
      // Nudge useAuth to re-check whoami; it'll see the new cookie +
      // role=kid and the shell will swap in the kid surfaces.
      await auth.refresh();
    } catch (e) {
      setErr((e as Error).message || "wrong PIN");
      setPin("");
      setSubmitting(false);
    }
  };

  if (loadErr) {
    // Server returned 404 — we're on the public hostname. Render
    // nothing here; the parent component falls back to adult login.
    return null;
  }

  if (!kids) {
    return (
      <div className="kid-login-page">
        <p className="kid-login-loading">Loading…</p>
      </div>
    );
  }

  if (kids.length === 0) {
    return (
      <div className="kid-login-page">
        <div className="kid-login-empty">
          <Icon name="people" size={36} />
          <h2>No kids yet</h2>
          <p>Ask a grown-up to add you in their Hub settings.</p>
          <button className="kid-login-back" onClick={onBack} type="button">
            <Icon name="arrow-left" size={14} /> Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // Selected → PIN entry
  if (selected) {
    return (
      <div className="kid-login-page" style={{ "--kid-color": selected.avatarColor || "var(--accent)" } as any}>
        <div className="kid-login-pin">
          <button className="kid-login-back" type="button" onClick={() => { setSelected(null); setPin(""); setErr(null); }}>
            <Icon name="arrow-left" size={14} />
          </button>

          <div
            className="kid-login-avatar kid-login-avatar-big"
            style={{ background: selected.avatarColor || "var(--accent)" } as any}
          >
            {selected.avatarEmoji || selected.displayName[0].toUpperCase()}
          </div>
          <h1 className="kid-login-name">Hi, {selected.displayName}!</h1>
          <p className="kid-login-sub">Type your PIN to come in.</p>

          {err && <div className="kid-login-err">{err}</div>}

          <div className="kid-login-pin-display">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="kid-login-pin-slot"
                data-filled={pin.length > i}
                data-active={pin.length === i}
                data-optional={i >= 4}
              />
            ))}
          </div>

          <div className="kid-login-keypad">
            {["1","2","3","4","5","6","7","8","9"].map((n) => (
              <button
                key={n}
                className="kid-login-key"
                type="button"
                onClick={() => { setErr(null); setPin((p) => (p.length < 6 ? p + n : p)); }}
              >
                {n}
              </button>
            ))}
            <button
              className="kid-login-key kid-login-key-clear"
              type="button"
              onClick={() => { setPin(""); setErr(null); }}
              aria-label="Clear"
            >
              <Icon name="x" size={20} />
            </button>
            <button
              className="kid-login-key"
              type="button"
              onClick={() => { setErr(null); setPin((p) => (p.length < 6 ? p + "0" : p)); }}
            >
              0
            </button>
            <button
              className="kid-login-key kid-login-key-submit"
              type="button"
              onClick={submit}
              disabled={pin.length < 4 || submitting}
            >
              <Icon name="arrow-right" size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Avatar grid — pick your face to start
  return (
    <div className="kid-login-page">
      <div className="kid-login-grid-wrap">
        <h1 className="kid-login-h1">Who's signing in?</h1>
        <p className="kid-login-sub">Pick your face to come in.</p>

        {gridErr && <div className="kid-login-err">{gridErr}</div>}

        <div className="kid-login-grid">
          {kids.map((k) => (
            <button
              key={k.id}
              className="kid-login-card"
              type="button"
              onClick={() => void onPick(k)}
              disabled={!!signingInId}
              data-busy={signingInId === k.id}
              style={{ "--kid-color": k.avatarColor || "var(--accent)" } as any}
            >
              <div
                className="kid-login-avatar"
                style={{ background: k.avatarColor || "var(--accent)" } as any}
              >
                {k.avatarEmoji || k.displayName[0].toUpperCase()}
              </div>
              <div className="kid-login-card-name">{k.displayName}</div>
            </button>
          ))}
        </div>

        {/* Escape hatch — a device paired into the household's kid mode
          * had no way out of this picker. The other two states (empty
          * household + PIN entry) already render a Back button; the
          * grid view was missing it, leaving anyone who needed to
          * re-pair as an adult stuck. Tapping this calls auth.logout
          * which clears the kid-device session cookie + bounces to
          * the adult login flow on next refresh. */}
        <button
          className="kid-login-grid-signout"
          type="button"
          onClick={onBack}
        >
          Sign out this device
        </button>
      </div>
    </div>
  );
}
